import React from 'react';
import { jsPDF } from 'jspdf';

/**
 * Driver Route Sheet PDF Generator
 * Generates printable PDF route sheets for drivers with daily, weekly, or monthly views
 */

export const generateDriverRouteSheetPDF = (driver, tasks, date, viewType = 'daily') => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Helper function to check if we need a new page
  const checkNewPage = (neededHeight = 15) => {
    if (yPosition + neededHeight > pageHeight - margin) {
      doc?.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Header
  doc?.setFontSize(20);
  doc?.setFont(undefined, 'bold');
  doc?.text('Feuille de Route', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Driver Info
  doc?.setFontSize(12);
  doc?.setFont(undefined, 'normal');
  doc?.text(`Chauffeur: ${driver?.name || 'Non assigné'}`, margin, yPosition);
  yPosition += 7;
  doc?.text(`Zone: ${driver?.zone || 'Non définie'}`, margin, yPosition);
  yPosition += 7;
  
  // Date/Period
  if (viewType === 'daily') {
    doc?.text(`Date: ${new Date(date)?.toLocaleDateString('fr-FR')}`, margin, yPosition);
  } else if (viewType === 'weekly') {
    const startDate = new Date(date);
    const endDate = new Date(date);
    endDate?.setDate(endDate?.getDate() + 6);
    doc?.text(`Semaine du ${startDate?.toLocaleDateString('fr-FR')} au ${endDate?.toLocaleDateString('fr-FR')}`, margin, yPosition);
  } else if (viewType === 'monthly') {
    doc?.text(`Mois: ${new Date(date)?.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`, margin, yPosition);
  }
  yPosition += 7;
  doc?.text(`Nombre de collectes: ${tasks?.length}`, margin, yPosition);
  yPosition += 12;

  // Separator line
  doc?.setLineWidth(0.5);
  doc?.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Tasks
  tasks?.forEach((task, index) => {
    checkNewPage(45);

    // Task number and time
    doc?.setFontSize(11);
    doc?.setFont(undefined, 'bold');
    doc?.text(`${index + 1}. ${task?.timeWindow || 'Heure non définie'}`, margin, yPosition);
    yPosition += 7;

    // Client info
    doc?.setFont(undefined, 'normal');
    doc?.setFontSize(10);
    doc?.text(`Client: ${task?.clientName}`, margin + 5, yPosition);
    yPosition += 6;
    
    if (task?.clientPhone) {
      doc?.text(`Tél: ${task?.clientPhone}`, margin + 5, yPosition);
      yPosition += 6;
    }

    // Address
    if (task?.clientAddress) {
      doc?.text(`Adresse: ${task?.clientAddress}`, margin + 5, yPosition);
      yPosition += 6;
    }
    if (task?.clientCity) {
      doc?.text(`Ville: ${task?.clientCity}${task?.clientZone ? ` - Zone ${task?.clientZone}` : ''}`, margin + 5, yPosition);
      yPosition += 6;
    }

    // Notes
    if (task?.notes) {
      doc?.setFont(undefined, 'italic');
      const notesLines = doc?.splitTextToSize(`Notes: ${task?.notes}`, pageWidth - margin * 2 - 10);
      notesLines?.forEach(line => {
        checkNewPage();
        doc?.text(line, margin + 5, yPosition);
        yPosition += 5;
      });
      doc?.setFont(undefined, 'normal');
    }

    // Status
    const statusColors = {
      'planifie': [59, 130, 246],
      'en_cours': [234, 179, 8],
      'termine': [34, 197, 94],
      'annule': [239, 68, 68]
    };
    const statusLabels = {
      'planifie': 'Planifié',
      'en_cours': 'En cours',
      'termine': 'Terminé',
      'annule': 'Annulé'
    };
    
    const color = statusColors?.[task?.status] || statusColors?.planifie;
    doc?.setFillColor(color?.[0], color?.[1], color?.[2]);
    doc?.roundedRect(margin + 5, yPosition - 3, 25, 5, 1, 1, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(8);
    doc?.text(statusLabels?.[task?.status] || 'Planifié', margin + 7, yPosition);
    doc?.setTextColor(0, 0, 0);
    doc?.setFontSize(10);
    yPosition += 8;

    // Signature box
    doc?.setLineWidth(0.3);
    doc?.rect(margin + 5, yPosition, 60, 20);
    doc?.setFontSize(8);
    doc?.text('Signature client:', margin + 7, yPosition + 5);
    yPosition += 25;

    // Separator
    if (index < tasks?.length - 1) {
      doc?.setLineWidth(0.2);
      doc?.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;
    }
  });

  // Footer on last page
  yPosition = pageHeight - margin;
  doc?.setFontSize(8);
  doc?.setFont(undefined, 'italic');
  doc?.text(`Généré le ${new Date()?.toLocaleString('fr-FR')}`, margin, yPosition);
  doc?.text(`Page ${doc?.internal?.getNumberOfPages()}`, pageWidth - margin - 15, yPosition);

  // Save PDF
  const filename = `feuille_route_${driver?.name?.replace(/\s+/g, '_')}_${new Date(date)?.toISOString()?.split('T')?.[0]}.pdf`;
  doc?.save(filename);
};

export default { generateDriverRouteSheetPDF };